import React, { useEffect, useMemo, useState } from 'react';
import { createApiClient } from '../lib/apiClient';
import { toast } from 'sonner';
import { Camera, Loader2, Eye, ScanLine, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = createApiClient();
export const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString() : '');
const inputCls = 'w-full border border-slate-300 rounded-md px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30';

export const openReceipt = async (expenseId) => {
  try {
    const r = await ax.get(`/fuel-receipts/${expenseId}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not open the receipt');
  }
};

export const useFuelOptions = (enabled = true) => {
  const [opts, setOpts] = useState({ drivers: [], trucks: [], loads: [], can_approve: false });
  useEffect(() => {
    if (!enabled) return;
    ax.get('/fuel-receipts/options').then((r) => setOpts(r.data)).catch(() => {});
  }, [enabled]);
  return opts;
};

const toForm = (exp) => ({
  expense_date: exp.expense_date || '', amount: exp.amount ? String(exp.amount) : '', fuel_gallons: exp.fuel_gallons ? String(exp.fuel_gallons) : '',
  fuel_station: exp.fuel_station || exp.vendor_name || '', truck_id: exp.truck_id || '', load_id: exp.load_id || '', paid_by: exp.paid_by || '', note: exp.note || '',
});

const PaidBy = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-3 text-sm" data-testid={testid}>
    <span className="text-xs font-semibold text-slate-600">Who paid?</span>
    {[['driver', 'Driver (out of pocket)'], ['company', 'Company (fuel card / account)']].map(([v, l]) => (
      <label key={v} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border cursor-pointer ${value === v ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]' : 'border-slate-300 text-slate-700'}`}>
        <input type="radio" name={`${testid}-paid-by`} value={v} checked={value === v} onChange={() => onChange(v)} data-testid={`${testid}-${v}`} className="accent-[#002FA7]" />{l}
      </label>
    ))}
  </div>
);

export const FuelReceiptConfirmForm = ({ expense, role, options, onSaved, onCancel, ocr }) => {
  const [form, setForm] = useState(toForm(expense));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(toForm(expense)); }, [expense.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isDriver = role === 'driver';
  const loads = useMemo(() => {
    const d = (options.drivers || []).find((x) => x.id === expense.driver_id);
    return (options.loads || []).filter((l) => !d || !l.driver_user_id || l.driver_user_id === d.user_id);
  }, [options, expense.driver_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.paid_by) { toast.error('Please select who paid for the fuel.'); return; }
    setSaving(true);
    try {
      const body = { expense_date: form.expense_date, amount: parseFloat(form.amount), fuel_gallons: form.fuel_gallons ? parseFloat(form.fuel_gallons) : null,
        fuel_station: form.fuel_station, truck_id: form.truck_id || null, load_id: form.load_id || null, paid_by: form.paid_by, note: form.note };
      const r = await ax.put(`/fuel-receipts/${expense.id}`, body);
      if (r.data.possible_duplicate) {
        toast.warning(`Heads up: a ${r.data.possible_duplicate.vendor_name || 'fuel'} receipt for ${money(r.data.possible_duplicate.amount)} on ${fmtDate(r.data.possible_duplicate.expense_date)} already exists. The office will confirm it is a separate purchase.`);
      }
      toast.success(isDriver ? 'Fuel receipt submitted for office review.' : 'Fuel receipt details saved.');
      onSaved?.(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Could not save receipt details');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} data-testid="fuel-confirm-form" className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid="fuel-confirm-banner">
        <ScanLine className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{ocr && (ocr.amount || ocr.vendor) ? `We read these values from the receipt (confidence ${ocr.confidence ?? 0}%). Please check every field against the paper receipt before submitting.` : 'We could not read this receipt automatically. Please enter the details from the paper receipt.'}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div><label className="text-[11px] font-semibold text-slate-500">Fueling date</label><input data-testid="fuel-date" type="date" required value={form.expense_date} onChange={set('expense_date')} className={inputCls} /></div>
        <div><label className="text-[11px] font-semibold text-slate-500">Total amount</label><input data-testid="fuel-amount" type="number" step="0.01" min="0.01" required value={form.amount} onChange={set('amount')} className={inputCls} /></div>
        <div><label className="text-[11px] font-semibold text-slate-500">Gallons</label><input data-testid="fuel-gallons" type="number" step="0.001" min="0" value={form.fuel_gallons} onChange={set('fuel_gallons')} className={inputCls} /></div>
        <div><label className="text-[11px] font-semibold text-slate-500">Fuel station</label><input data-testid="fuel-station" required placeholder="e.g. Pilot #412, Memphis TN" value={form.fuel_station} onChange={set('fuel_station')} className={inputCls} /></div>
        <div><label className="text-[11px] font-semibold text-slate-500">Truck</label>
          <select data-testid="fuel-truck" value={form.truck_id} onChange={set('truck_id')} className={inputCls}>
            <option value="">— none —</option>
            {(options.trucks || []).map((t) => <option key={t.id} value={t.id}>{t.unit || t.id.slice(-6)}</option>)}
          </select></div>
        <div className="col-span-2 md:col-span-2"><label className="text-[11px] font-semibold text-slate-500">Trip / load (optional)</label>
          <select data-testid="fuel-load" value={form.load_id} onChange={set('load_id')} className={inputCls}>
            <option value="">— not tied to a load —</option>
            {loads.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select></div>
        <div><label className="text-[11px] font-semibold text-slate-500">Note</label><input data-testid="fuel-note" value={form.note} onChange={set('note')} className={inputCls} placeholder="Optional" /></div>
      </div>
      <PaidBy value={form.paid_by} onChange={(v) => setForm({ ...form, paid_by: v })} testid="fuel-paid-by" />
      {!isDriver && <p className="text-[11px] text-slate-500">Deductibility from driver pay is decided by a company administrator at approval time — it is never assumed.</p>}
      <div className="flex items-center justify-between gap-2 pt-1">
        <button type="button" data-testid="fuel-view-receipt" onClick={() => openReceipt(expense.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"><Eye className="w-3.5 h-3.5" /> View uploaded receipt</button>
        <div className="flex gap-2">
          {onCancel && <button type="button" onClick={onCancel} className="px-3 py-2 text-sm rounded-md border border-slate-300">Cancel</button>}
          <button data-testid="fuel-confirm-submit" type="submit" disabled={saving} className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-md bg-[#002FA7] text-white hover:bg-[#00247D] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}{isDriver ? 'Confirm & submit' : 'Save details'}
          </button>
        </div>
      </div>
    </form>
  );
};

export const FuelReceiptUploader = ({ role, driverId = '', driverUserId = '', loadId = '', truckId = '', onSaved, onCancel }) => {
  const options = useFuelOptions(true);
  const isStaff = role !== 'driver';
  const [pick, setPick] = useState({ driver_id: driverId, load_id: loadId, truck_id: truckId });
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // { duplicate, expense, ocr }
  const driverLocked = !!driverId || (!!driverUserId && options.drivers.some((d) => d.user_id === driverUserId));

  useEffect(() => {
    if (!isStaff || !options.drivers?.length) return;
    setPick((p) => {
      const next = { ...p };
      if (!next.driver_id && driverUserId) {
        const d = options.drivers.find((x) => x.user_id === driverUserId);
        if (d) next.driver_id = d.id;
      }
      if (!next.driver_id && next.load_id) {
        const ld = options.loads.find((l) => l.id === next.load_id);
        const d = ld && options.drivers.find((x) => x.user_id === ld.driver_user_id);
        if (d) next.driver_id = d.id;
      }
      if (!next.truck_id) {
        const ld = options.loads.find((l) => l.id === next.load_id);
        const d = options.drivers.find((x) => x.id === next.driver_id);
        next.truck_id = ld?.truck_id || d?.truck_id || '';
      }
      return next;
    });
  }, [options, isStaff, driverUserId]);

  const upload = async (file) => {
    if (!file) return;
    if (isStaff && !pick.driver_id) { toast.error('Select the driver this fuel receipt belongs to.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (isStaff) fd.append('driver_id', pick.driver_id);
      if (pick.load_id) fd.append('load_id', pick.load_id);
      if (pick.truck_id) fd.append('truck_id', pick.truck_id);
      const r = await ax.post('/fuel-receipts/upload', fd);
      setResult(r.data);
      if (r.data.duplicate) toast.warning(r.data.message);
      else if (r.data.converted_from_heic) toast.success('HEIC photo converted to JPG.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Upload failed. Try again.');
    } finally { setUploading(false); }
  };

  if (result?.duplicate) {
    const ex = result.expense;
    return (
      <div data-testid="fuel-duplicate-notice" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
        <div className="flex items-start gap-2"><AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">This receipt was already uploaded</p>
            <p className="text-xs text-amber-800 mt-1">Existing record: {fmtDate(ex.expense_date)} · {ex.fuel_station || ex.vendor_name || 'Fuel'} · {money(ex.amount)} · status <b>{ex.review_status}</b>{ex.load_ref ? ` · Load ${ex.load_ref}` : ''}. No second expense or payroll charge was created.</p>
            <div className="flex gap-3 mt-2">
              <button type="button" data-testid="fuel-duplicate-view" onClick={() => openReceipt(ex.id)} className="text-xs font-semibold text-[#002FA7] hover:underline">View existing receipt</button>
              <button type="button" data-testid="fuel-duplicate-close" onClick={() => { setResult(null); onSaved?.(ex); }} className="text-xs font-semibold text-slate-700 hover:underline">Done</button>
            </div>
          </div></div>
      </div>
    );
  }
  if (result?.expense) {
    return <FuelReceiptConfirmForm key={result.expense.id} expense={result.expense} role={role} options={options} ocr={result.ocr} onCancel={onCancel}
      onSaved={(exp) => { setResult(null); onSaved?.(exp); }} />;
  }

  const pickCls = inputCls;
  return (
    <div data-testid="fuel-uploader" className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      {isStaff && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select data-testid="fuel-pick-driver" value={pick.driver_id} onChange={(e) => { const d = options.drivers.find((x) => x.id === e.target.value); setPick({ ...pick, driver_id: e.target.value, truck_id: d?.truck_id || pick.truck_id }); }} className={pickCls} disabled={driverLocked}>
            <option value="">Select driver / owner-operator…</option>
            {options.drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.truck_unit ? ` · ${d.truck_unit}` : ''}</option>)}
          </select>
          <select data-testid="fuel-pick-load" value={pick.load_id} onChange={(e) => setPick({ ...pick, load_id: e.target.value })} className={pickCls} disabled={!!loadId}>
            <option value="">No specific load</option>
            {options.loads.filter((l) => { const d = options.drivers.find((x) => x.id === pick.driver_id); return !d || !l.driver_user_id || l.driver_user_id === d.user_id; }).map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
          <select data-testid="fuel-pick-truck" value={pick.truck_id} onChange={(e) => setPick({ ...pick, truck_id: e.target.value })} className={pickCls}>
            <option value="">Truck (optional)</option>
            {options.trucks.map((t) => <option key={t.id} value={t.id}>{t.unit || t.id.slice(-6)}</option>)}
          </select>
        </div>
      )}
      <label data-testid="fuel-upload-label" className={`flex items-center justify-center gap-2 bg-[#002FA7] text-white px-4 py-3 rounded-md text-sm font-semibold cursor-pointer hover:bg-[#00247D] ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        {uploading ? 'Uploading & reading receipt…' : 'Take photo / choose fuel receipt'}
        <input data-testid="fuel-file-input" type="file" accept="image/*,.heic,.heif,application/pdf,.pdf" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      <p className="text-[11px] text-slate-500">JPG, PNG, HEIC (iPhone) or PDF up to 15MB. You will confirm the date, total, gallons and station on the next step.</p>
      {onCancel && <button type="button" onClick={onCancel} className="text-xs text-slate-600 hover:underline">Cancel</button>}
    </div>
  );
};
